import * as Sentry from '@sentry/nextjs';
import Layout from 'components/layout';
import useMediaQuery from 'hooks/useMediaQuery';
import { getLetters } from 'lib/api/letters';
import dynamic from 'next/dynamic';
import { useRef } from 'react';
import { useIntersection } from 'use-intersection';

import { isWebglSupported } from 'utilities/dom';
import { NewspapersType } from 'utilities/propTypes';

// We do two things here:
// 1. Dynamically import the map, so server-side never loads/packages it
// 2. Instantiate the map once it's about to come into view, deferring load at all
// It's possible this isn't quite optimal but seems to do the trick
const MapPlaceholder = () => <p>Reticulating splines...</p>;
const DynamicMap = dynamic(() => import('components/Map'), {
  ssr: false,
  loading: () => <MapPlaceholder />,
});
const MapLoader = ({ letters }) => {
  const mapRef = useRef();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const mapStyle = prefersDark ? 'dark-v9' : 'light-v10';

  const isIntersecting = useIntersection(mapRef, {
    once: true,
    threshold: 0.1,
  });
  const map = (
    <div ref={mapRef}>
      {isIntersecting && isWebglSupported() ? (
        <DynamicMap
          styles={[mapStyle]}
          center={[-98.583333, 38.5]}
          zoom={3}
          scrollZoom={false}
          letters={letters}
        />
      ) : null}
    </div>
  );

  return map;
};

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(
  async ({ preview = false }) => {
    const lettersQuery = await getLetters({ preview: false });

    return {
      props: {
        letters: lettersQuery,
        preview,
      },
    };
  }
);

const Home = (props) => {
  const { letters } = props;

  return (
    <Layout
      title="Letters to the Editor Published by Resistbot"
      description="We’ve published thousands of members’ letters calling out elected
      officials in local newspapers in 49 states and D.C."
    >
      <h1 className="mt-6">See your Letter in Your Local&nbsp;Newspaper</h1>
      <p className="mb-4">
        We’ve published thousands of members’ letters calling out elected
        officials in local newspapers in 49 states and&nbsp;D.C.
      </p>

      <MapLoader letters={letters} />
    </Layout>
  );
};

Home.propTypes = {
  letters: NewspapersType.isRequired,
};

export default Home;
